"use client";

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { FuturIASidebar } from "@/components/futur-ia-oggi";
import { createAzienda, listAziende, type Azienda } from "@/mock-api/aziende";

type AziendaForm = { nome: string; settore: string; referente: string; email: string };

const emptyForm: AziendaForm = { nome: "", settore: "", referente: "", email: "" };

export function AziendeScreen() {
  const [aziende, setAziende] = useState<Azienda[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Azienda | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState<AziendaForm>(emptyForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof AziendaForm, string>>>({});
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  const loadAziende = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setAziende(await listAziende());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // The delayed mock request intentionally starts when the screen mounts.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void loadAziende(); }, [loadAziende]);

  useEffect(() => {
    if (!addOpen && !selected) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAddOpen(false);
        setSelected(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [addOpen, selected]);

  const normalizedQuery = query.trim().toLocaleLowerCase("it");
  const filtered = aziende.filter((azienda) =>
    `${azienda.nome} ${azienda.settore} ${azienda.referente}`.toLocaleLowerCase("it").includes(normalizedQuery),
  );

  function closeAdd() {
    setAddOpen(false);
    setForm(emptyForm);
    setFormErrors({});
    setFormError("");
  }

  function validate() {
    const next: Partial<Record<keyof AziendaForm, string>> = {};
    (Object.keys(form) as Array<keyof AziendaForm>).forEach((field) => {
      if (!form[field].trim()) next[field] = "Campo obbligatorio";
    });
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      next.email = "Inserisci un indirizzo email valido";
    }
    setFormErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    if (!validate() || formLoading) return;
    setFormLoading(true);
    try {
      const created = await createAzienda(form);
      setAziende((current) => [created, ...current]);
      closeAdd();
      setFeedback(`${created.nome} è stata aggiunta alle aziende.`);
    } catch {
      setFormError("Non è stato possibile aggiungere l’azienda. Riprova.");
    } finally {
      setFormLoading(false);
    }
  }

  function updateField(field: keyof AziendaForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: undefined }));
    setFormError("");
  }

  return <main className="futur-ia-app aziende-app">
    <FuturIASidebar activeHref="/aziende" />
    <section className="futur-ia-main">
      <header className="futur-ia-topbar"><h1>Aziende</h1></header>
      <div className="futur-ia-content aziende-content">
        <header className="aziende-page-heading">
          <div>
            <p className="aziende-eyebrow">ANAGRAFICA DELLO STUDIO · DEMO</p>
            <h2>Clienti dello studio</h2>
            <p>I clienti dello studio, con pratiche e documenti collegati in un unico punto.</p>
          </div>
          <button className="aziende-add-button" type="button" onClick={() => { setAddOpen(true); setFeedback(""); }}>
            <span aria-hidden="true">＋</span> Aggiungi azienda
          </button>
        </header>

        {feedback && <div className="aziende-feedback" role="status"><span>{feedback}</span><button type="button" aria-label="Chiudi messaggio" onClick={() => setFeedback("")}>×</button></div>}

        <div className="aziende-toolbar">
          <label className="sr-only" htmlFor="aziende-search">Cerca azienda, settore o referente</label>
          <span className="aziende-search-icon" aria-hidden="true">⌕</span>
          <input id="aziende-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca azienda, settore o referente…" autoComplete="off" />
          <span className="aziende-count" aria-live="polite">{filtered.length} {filtered.length === 1 ? "azienda" : "aziende"} demo</span>
        </div>

        {loading && <div className="aziende-state" role="status"><span className="aziende-loading-mark" aria-hidden="true">•••</span><strong>Caricamento aziende…</strong><p>Sto preparando l&apos;anagrafica demo.</p></div>}
        {!loading && error && <div className="aziende-state is-error" role="alert"><span className="aziende-state-mark" aria-hidden="true">!</span><div><strong>Impossibile caricare le aziende</strong><p>L&apos;anagrafica demo non è disponibile in questo momento.</p><button className="aziende-secondary-button" type="button" onClick={() => void loadAziende()}>Riprova</button></div></div>}
        {!loading && !error && filtered.length === 0 && <div className="aziende-state" role="status"><span className="aziende-state-mark" aria-hidden="true">⌕</span><strong>{normalizedQuery ? "Nessuna azienda trovata" : "Nessuna azienda nell’anagrafica"}</strong><p>{normalizedQuery ? "Modifica la ricerca per trovare un cliente dello studio." : "Le aziende aggiunte appariranno qui."}</p></div>}

        {!loading && !error && filtered.length > 0 && <section className="aziende-list" aria-label="Aziende dello studio">
          {filtered.map((azienda) => <button className="azienda-row" type="button" key={azienda.id} onClick={() => setSelected(azienda)} aria-label={`Apri ${azienda.nome}`}>
            <span className="azienda-avatar" aria-hidden="true">{azienda.nome.slice(0, 1)}</span>
            <span className="azienda-main"><strong>{azienda.nome}</strong><span>{azienda.settore}</span><small>Referente: {azienda.referente}</small></span>
            <span className="azienda-stats"><strong>{azienda.pratiche} {azienda.pratiche === 1 ? "pratica" : "pratiche"}</strong><span>{azienda.documenti} {azienda.documenti === 1 ? "documento" : "documenti"}</span></span>
            <span className={`azienda-status ${azienda.stato === "Attiva" ? "is-active" : "is-pending"}`}>{azienda.stato}</span>
            <span className="azienda-arrow" aria-hidden="true">›</span>
          </button>)}
        </section>}

        <p className="aziende-demo-caption">Anteprima <span>·</span> Dati dimostrativi</p>
      </div>
    </section>

    {selected && <div className="aziende-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
      <aside className="aziende-drawer" role="dialog" aria-modal="true" aria-labelledby="azienda-detail-title">
        <button className="aziende-close" type="button" onClick={() => setSelected(null)} aria-label="Chiudi dettaglio azienda">×</button>
        <p className="aziende-eyebrow">DETTAGLIO AZIENDA · DEMO</p>
        <div className="azienda-detail-heading"><span className="azienda-avatar" aria-hidden="true">{selected.nome.slice(0, 1)}</span><div><h2 id="azienda-detail-title">{selected.nome}</h2><span className={`azienda-status ${selected.stato === "Attiva" ? "is-active" : "is-pending"}`}>{selected.stato}</span></div></div>
        <p className="azienda-detail-lead">{selected.settore}</p>
        <dl className="azienda-detail-facts">
          <div><dt>Referente</dt><dd>{selected.referente}</dd></div>
          <div><dt>Email</dt><dd><a href={`mailto:${selected.email}`}>{selected.email}</a></dd></div>
          <div><dt>Pratiche</dt><dd>{selected.pratiche}</dd></div>
          <div><dt>Documenti</dt><dd>{selected.documenti}</dd></div>
        </dl>
        <h3>Attività recente</h3>
        <p className="azienda-detail-activity">{selected.ultimaAttivita}</p>
        {selected.id === "alfa" && <><h3>Apri raccolta</h3><div className="azienda-related-links"><a href="/pratiche">Pratiche <span>{selected.pratiche} →</span></a><a href="/documents">Documenti <span>{selected.documenti} →</span></a></div></>}
        <p className="azienda-detail-note">Dati dimostrativi: le informazioni di questa scheda non sono archiviate.</p>
      </aside>
    </div>}

    {addOpen && <div className="aziende-overlay aziende-modal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeAdd(); }}>
      <section className="aziende-modal" role="dialog" aria-modal="true" aria-labelledby="azienda-form-title">
        <button className="aziende-close" type="button" onClick={closeAdd} aria-label="Chiudi aggiunta azienda">×</button>
        <p className="aziende-eyebrow">NUOVA AZIENDA · DEMO</p>
        <h2 id="azienda-form-title">Aggiungi un cliente</h2>
        <p className="aziende-modal-copy">Inserisci i dati principali per aggiungere un&apos;azienda all&apos;anagrafica dimostrativa.</p>
        {formError && <p className="aziende-form-error" role="alert">{formError}</p>}
        <form onSubmit={submit} noValidate>
          <label htmlFor="azienda-nome">Nome azienda<input id="azienda-nome" autoFocus value={form.nome} onChange={(event) => updateField("nome", event.target.value)} aria-invalid={Boolean(formErrors.nome)} aria-describedby={formErrors.nome ? "azienda-nome-error" : undefined} />{formErrors.nome && <small id="azienda-nome-error" className="azienda-field-error">{formErrors.nome}</small>}</label>
          <label htmlFor="azienda-settore">Settore<input id="azienda-settore" value={form.settore} onChange={(event) => updateField("settore", event.target.value)} aria-invalid={Boolean(formErrors.settore)} aria-describedby={formErrors.settore ? "azienda-settore-error" : undefined} />{formErrors.settore && <small id="azienda-settore-error" className="azienda-field-error">{formErrors.settore}</small>}</label>
          <label htmlFor="azienda-referente">Referente<input id="azienda-referente" value={form.referente} onChange={(event) => updateField("referente", event.target.value)} aria-invalid={Boolean(formErrors.referente)} aria-describedby={formErrors.referente ? "azienda-referente-error" : undefined} />{formErrors.referente && <small id="azienda-referente-error" className="azienda-field-error">{formErrors.referente}</small>}</label>
          <label htmlFor="azienda-email">Email<input id="azienda-email" type="email" autoComplete="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} aria-invalid={Boolean(formErrors.email)} aria-describedby={formErrors.email ? "azienda-email-error" : undefined} />{formErrors.email && <small id="azienda-email-error" className="azienda-field-error">{formErrors.email}</small>}</label>
          <div className="aziende-form-actions"><button className="aziende-secondary-button" type="button" onClick={closeAdd} disabled={formLoading}>Annulla</button><button className="aziende-primary-button" type="submit" disabled={formLoading}>{formLoading ? "Aggiunta in corso…" : "Aggiungi azienda"}</button></div>
        </form>
      </section>
    </div>}
  </main>;
}
